import React, { useEffect, useState } from "react";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Lock, Loader2, RefreshCw, Mail, Search, LogOut, ArrowRight, Package, Tag, Boxes, Truck, Plus, Trash2, X, BarChart3, Printer, Inbox, FileText } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import Analytics from "../components/Analytics";
import LeadsTab from "../components/LeadsTab";
import PackageOrdersTab from "../components/PackageOrdersTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_OPTIONS = ["all", "received", "paid", "fulfilling", "shipped", "cancelled"];
const TABS = [
  { id: "orders", label: "Orders", icon: Package },
  { id: "packages", label: "Package Orders", icon: FileText },
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "promos", label: "Promo Codes", icon: Tag },
  { id: "inventory", label: "Inventory", icon: Boxes },
];

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("dms_admin_token") || "");
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("orders");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const login = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      setToken(data.token); sessionStorage.setItem("dms_admin_token", data.token);
    } catch { toast({ title: "Wrong password", variant: "destructive" }); }
    finally { setLoading(false); }
  };
  const logout = () => { setToken(""); sessionStorage.removeItem("dms_admin_token"); };

  if (!token) {
    return (
      <div className="min-h-screen bg-[color:var(--dms-cream)]">
        <Navbar />
        <section className="max-w-md mx-auto px-5 py-24">
          <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-8">
            <div className="w-14 h-14 rounded-full bg-[color:var(--dms-peach)] mx-auto flex items-center justify-center"><Lock className="w-6 h-6 text-[color:var(--dms-navy)]" /></div>
            <h1 className="font-serif text-3xl text-[color:var(--dms-navy)] text-center mt-5">Admin login</h1>
            <p className="mt-2 text-sm text-[color:var(--dms-navy)]/60 text-center">Enter the admin password to review orders.</p>
            <form onSubmit={login} className="mt-6 space-y-4">
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="w-full rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-3 focus:outline-none focus:border-[color:var(--dms-peach)]" />
              <button type="submit" disabled={loading} className="btn-peach w-full justify-center">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--dms-cream)]">
      <Navbar />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="kicker">Admin</p>
            <h1 className="font-serif text-4xl text-[color:var(--dms-navy)] mt-2">Design My Story console</h1>
          </div>
          <button onClick={logout} className="text-[color:var(--dms-navy)]/60 text-sm hover:text-[color:var(--dms-navy)] flex items-center gap-1"><LogOut className="w-4 h-4" /> Log out</button>
        </div>

        <div className="mt-6 flex items-center gap-2 flex-wrap">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded-full text-sm font-medium transition flex items-center gap-2 ${tab === t.id ? "bg-[color:var(--dms-navy)] text-white" : "bg-white border border-[color:var(--dms-navy)]/15 text-[color:var(--dms-navy)]"}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          {tab === "orders" && <OrdersTab token={token} onLogout={logout} />}
          {tab === "packages" && <PackageOrdersTab token={token} />}
          {tab === "leads" && <LeadsTab token={token} />}
          {tab === "analytics" && <Analytics token={token} />}
          {tab === "promos" && <PromosTab token={token} />}
          {tab === "inventory" && <InventoryTab token={token} />}
        </div>
      </section>
      <Footer />
    </div>
  );
}

// --------- Orders tab ---------
function OrdersTab({ token, onLogout }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [shipDialog, setShipDialog] = useState(null); // order_number
  const { toast } = useToast();

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders`, {
        headers: { "X-Admin-Password": token },
        params: { status: filter, q: q || undefined },
      });
      setOrders(data.orders);
    } catch (e) { if (e.response?.status === 401) onLogout(); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchOrders(); /* eslint-disable-next-line */ }, [filter]);

  const resend = async (on) => {
    try {
      await axios.post(`${API}/admin/orders/${on}/resend`, {}, { headers: { "X-Admin-Password": token } });
      toast({ title: `Emails re-sent for #${on}` });
    } catch { toast({ title: "Resend failed", variant: "destructive" }); }
  };

  const updateStatus = async (on, status, extra = {}) => {
    try {
      await axios.patch(`${API}/admin/orders/${on}/status`, { status, ...extra }, { headers: { "X-Admin-Password": token } });
      fetchOrders();
      toast({ title: `#${on} → ${status}${extra.tracking_number ? " · tracking added" : ""}` });
    } catch (e) { toast({ title: e.response?.data?.detail || "Update failed", variant: "destructive" }); }
  };

  const onStatusChange = (o, newStatus) => {
    if (newStatus === "shipped") { setShipDialog(o.order_number); return; }
    updateStatus(o.order_number, newStatus);
  };

  return (
    <>
      <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-5 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 bg-[color:var(--dms-cream-2)] rounded-full px-4 py-2 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[color:var(--dms-navy)]/50" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchOrders()}
            placeholder="Search order #, name or email" className="flex-1 bg-transparent focus:outline-none text-sm text-[color:var(--dms-navy)]" />
          <button onClick={fetchOrders} className="text-xs tracking-widest uppercase font-semibold text-[color:var(--dms-peach)]">Go</button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {STATUS_OPTIONS.map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${filter === s ? "bg-[color:var(--dms-navy)] text-white" : "bg-white border border-[color:var(--dms-navy)]/15 text-[color:var(--dms-navy)]"}`}>{s}</button>
          ))}
          <button onClick={fetchOrders} className="btn-ghost-navy !py-1.5 !px-3 text-[11px]"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--dms-peach)] mx-auto" /></div>
        ) : orders.length === 0 ? (
          <div className="p-12 text-center text-sm text-[color:var(--dms-navy)]/60">No orders match.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="bg-[color:var(--dms-cream-2)] text-[color:var(--dms-navy)] text-left">
                <tr>
                  <Th>Order</Th><Th>Customer</Th><Th>Items</Th><Th>Total</Th><Th>Payment</Th><Th>Status</Th><Th></Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--dms-navy)]/5">
                {orders.map((o) => (
                  <React.Fragment key={o.id}>
                    <tr className="hover:bg-[color:var(--dms-cream-2)]/40">
                      <td className="px-5 py-4 font-serif text-[color:var(--dms-navy)]">#{o.order_number}<div className="text-[10px] text-[color:var(--dms-navy)]/50 mt-1">{new Date(o.created_at).toLocaleString()}</div></td>
                      <td className="px-5 py-4"><div className="text-[color:var(--dms-navy)]">{o.customer_name}</div><div className="text-xs text-[color:var(--dms-navy)]/60">{o.customer_email}</div></td>
                      <td className="px-5 py-4 text-[color:var(--dms-navy)]/80">{o.items.length} item{o.items.length === 1 ? "" : "s"}</td>
                      <td className="px-5 py-4 font-serif text-[color:var(--dms-navy)]">${o.total.toFixed(2)}</td>
                      <td className="px-5 py-4"><Pill status={o.payment_status || "pending"} /></td>
                      <td className="px-5 py-4">
                        <select value={o.status} onChange={(e) => onStatusChange(o, e.target.value)} className="text-xs rounded-full border border-[color:var(--dms-navy)]/15 bg-white px-3 py-1.5 focus:outline-none focus:border-[color:var(--dms-peach)]">
                          {["received", "paid", "fulfilling", "shipped", "cancelled"].map((s) => (<option key={s} value={s}>{s}</option>))}
                        </select>
                        {o.tracking_number && <div className="text-[10px] text-[color:var(--dms-navy)]/50 mt-1 flex items-center gap-1"><Truck className="w-3 h-3" /> {o.tracking_number}</div>}
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <button onClick={() => setOpen(open === o.order_number ? null : o.order_number)} className="text-xs tracking-widest uppercase font-semibold text-[color:var(--dms-navy)] hover:text-[color:var(--dms-peach)] mr-3">{open === o.order_number ? "Hide" : "View"}</button>
                        <a href={`${API}/admin/orders/${o.order_number}/slip?password=${encodeURIComponent(token)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs tracking-widest uppercase font-semibold text-[color:var(--dms-navy)] hover:text-[color:var(--dms-peach)] mr-3"><Printer className="w-3.5 h-3.5" /> Slip</a>
                        <button onClick={() => resend(o.order_number)} className="inline-flex items-center gap-1 text-xs tracking-widest uppercase font-semibold text-[color:var(--dms-peach)] hover:text-[color:var(--dms-navy)]"><Mail className="w-3.5 h-3.5" /> Resend</button>
                      </td>
                    </tr>
                    {open === o.order_number && <ExpandedRow o={o} token={token} />}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {shipDialog && (
        <ShippingDialog orderNumber={shipDialog} onClose={() => setShipDialog(null)}
          onSave={(fields) => { updateStatus(shipDialog, "shipped", fields); setShipDialog(null); }} />
      )}
    </>
  );
}

function ExpandedRow({ o, token }) {
  return (
    <tr className="bg-[color:var(--dms-cream)]"><td colSpan={7} className="px-5 py-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">Shipping address</p>
          <p className="mt-2 text-sm text-[color:var(--dms-navy)]/80 leading-relaxed">{o.shipping_address}<br />{o.shipping_city}, {o.shipping_state} {o.shipping_zip}<br />{o.shipping_country}<br /><span className="text-xs text-[color:var(--dms-navy)]/60">{o.customer_phone || "—"}</span></p>
        </div>
        <div>
          <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">Notes</p>
          <p className="mt-2 text-sm text-[color:var(--dms-navy)]/80">{o.notes || "—"}</p>
          {o.tracking_number && (
            <>
              <p className="mt-4 text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">Tracking</p>
              <p className="mt-2 text-sm text-[color:var(--dms-navy)]/80">{o.tracking_carrier || "Carrier"}: <span className="font-serif">{o.tracking_number}</span>{o.tracking_url && <> · <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-[color:var(--dms-peach)] underline">track</a></>}</p>
            </>
          )}
        </div>
        <div>
          <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">Totals</p>
          <div className="mt-2 text-sm text-[color:var(--dms-navy)]/80 space-y-1">
            <div className="flex justify-between"><span>Subtotal</span><span>${o.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>${o.shipping.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Tax</span><span>${o.tax.toFixed(2)}</span></div>
            {o.discount_amount > 0 && <div className="flex justify-between"><span>Discount{o.discount_code ? ` (${o.discount_code})` : ""}</span><span>− ${o.discount_amount.toFixed(2)}</span></div>}
            <div className="flex justify-between font-serif text-[color:var(--dms-navy)] text-base pt-1 border-t border-[color:var(--dms-navy)]/10"><span>Total</span><span>${o.total.toFixed(2)}</span></div>
          </div>
        </div>
      </div>
      <div className="mt-5">
        <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">Items</p>
        <ul className="mt-2 divide-y divide-[color:var(--dms-navy)]/10">
          {o.items.map((it, i) => (<li key={i} className="py-2 flex items-center gap-3 text-sm">
            {it.img && <div className="w-10 h-10 rounded bg-[color:var(--dms-peach-light)]/40 overflow-hidden flex-shrink-0"><img src={it.img} alt="" className="w-full h-full object-cover" /></div>}
            <div className="flex-1 text-[color:var(--dms-navy)]/80">{it.title}<div className="text-[11px] text-[color:var(--dms-navy)]/55">{it.color_name} · {it.size} · x{it.qty}{it.customization ? ` · ${it.customization}` : ""}</div>
              {it.design_file_id && <a href={`${API}/admin/design-files/${it.design_file_id}?password=${encodeURIComponent(token)}`} target="_blank" rel="noreferrer" data-testid={`admin-design-file-${i}`} className="text-[11px] text-[color:var(--dms-peach)] underline underline-offset-4">Open design file: {it.design_file_name}</a>}
              {it.design_notes && <div className="text-[11px] text-[color:var(--dms-navy)]/70 italic" data-testid={`admin-design-notes-${i}`}>Design request: {it.design_notes}</div>}
            </div>
            <div className="font-serif text-[color:var(--dms-navy)]">${(it.price * it.qty).toFixed(2)}</div>
          </li>))}
        </ul>
      </div>
    </td></tr>
  );
}

function ShippingDialog({ orderNumber, onClose, onSave }) {
  const [tn, setTn] = useState("");
  const [tc, setTc] = useState("USPS");
  const [tu, setTu] = useState("");

  const save = () => {
    if (!tn.trim()) return;
    onSave({ tracking_number: tn.trim(), tracking_carrier: tc, tracking_url: tu.trim() || null });
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[color:var(--dms-navy)]/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <p className="kicker">Mark as shipped</p>
            <h3 className="font-serif text-2xl text-[color:var(--dms-navy)] mt-1">Add tracking for #{orderNumber}</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[color:var(--dms-navy)]/60" /></button>
        </div>
        <p className="mt-3 text-sm text-[color:var(--dms-navy)]/60">We'll email the customer the tracking details right away.</p>
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">Tracking number *</span>
            <input value={tn} onChange={(e) => setTn(e.target.value)} className="mt-2 w-full rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-2.5 focus:outline-none focus:border-[color:var(--dms-peach)]" />
          </label>
          <label className="block">
            <span className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">Carrier</span>
            <select value={tc} onChange={(e) => setTc(e.target.value)} className="mt-2 w-full rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-2.5 focus:outline-none focus:border-[color:var(--dms-peach)]">
              {["USPS", "UPS", "FedEx", "DHL", "Other"].map((c) => (<option key={c}>{c}</option>))}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">Tracking URL (optional)</span>
            <input value={tu} onChange={(e) => setTu(e.target.value)} placeholder="https://..." className="mt-2 w-full rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-2.5 focus:outline-none focus:border-[color:var(--dms-peach)]" />
          </label>
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onClose} className="btn-ghost-navy !py-2.5 !px-5 text-[11px]">Cancel</button>
          <button onClick={save} disabled={!tn.trim()} className="btn-peach disabled:opacity-50">Ship & email customer <ArrowRight className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

// --------- Promos tab ---------
function PromosTab({ token }) {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ code: "", kind: "percent", value: 10, min_subtotal: 0, max_uses: "", expires_at: "" });
  const { toast } = useToast();

  const fetchPromos = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/promos`, { headers: { "X-Admin-Password": token } });
      setPromos(data.promos);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchPromos(); /* eslint-disable-next-line */ }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { code: form.code.toUpperCase(), kind: form.kind, value: parseFloat(form.value),
                        min_subtotal: parseFloat(form.min_subtotal || 0),
                        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
                        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null, active: true };
      await axios.post(`${API}/admin/promos`, payload, { headers: { "X-Admin-Password": token } });
      setForm({ code: "", kind: "percent", value: 10, min_subtotal: 0, max_uses: "", expires_at: "" });
      fetchPromos();
      toast({ title: "Promo code created" });
    } catch (err) {
      toast({ title: err.response?.data?.detail || "Create failed", variant: "destructive" });
    }
  };

  const del = async (code) => {
    try {
      await axios.delete(`${API}/admin/promos/${code}`, { headers: { "X-Admin-Password": token } });
      fetchPromos();
    } catch { toast({ title: "Delete failed", variant: "destructive" }); }
  };

  return (
    <>
      <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-6">
        <h3 className="font-serif text-xl text-[color:var(--dms-navy)]">Create a promo code</h3>
        <form onSubmit={create} className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="CODE (e.g. SAVE10)" className="rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--dms-peach)] font-mono" />
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-2.5 text-sm">
            <option value="percent">% off</option><option value="flat">$ flat</option>
          </select>
          <input required type="number" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="Value" className="rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-2.5 text-sm" />
          <input type="number" step="0.01" value={form.min_subtotal} onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })} placeholder="Min subtotal" className="rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-2.5 text-sm" />
          <input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="Max uses" className="rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-2.5 text-sm" />
          <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-2.5 text-sm" />
          <button type="submit" className="btn-peach sm:col-span-2 lg:col-span-6 justify-center"><Plus className="w-4 h-4" /> Create code</button>
        </form>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 overflow-hidden">
        {loading ? <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--dms-peach)] mx-auto" /></div>
          : promos.length === 0 ? <div className="p-12 text-center text-sm text-[color:var(--dms-navy)]/60">No promo codes yet. Create your first above.</div>
          : (
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--dms-cream-2)] text-left"><tr>
              <Th>Code</Th><Th>Discount</Th><Th>Min subtotal</Th><Th>Uses</Th><Th>Expires</Th><Th></Th>
            </tr></thead>
            <tbody className="divide-y divide-[color:var(--dms-navy)]/5">
              {promos.map((p) => (
                <tr key={p.code}>
                  <td className="px-5 py-4 font-mono text-[color:var(--dms-navy)]">{p.code}</td>
                  <td className="px-5 py-4 text-[color:var(--dms-navy)]/80">{p.kind === "percent" ? `${p.value}% off` : `$${p.value.toFixed(2)} off`}</td>
                  <td className="px-5 py-4 text-[color:var(--dms-navy)]/80">${(p.min_subtotal || 0).toFixed(2)}</td>
                  <td className="px-5 py-4 text-[color:var(--dms-navy)]/80">{p.used_count || 0}{p.max_uses ? ` / ${p.max_uses}` : ""}</td>
                  <td className="px-5 py-4 text-[color:var(--dms-navy)]/80">{p.expires_at ? new Date(p.expires_at).toLocaleDateString() : "—"}</td>
                  <td className="px-5 py-4 text-right"><button onClick={() => del(p.code)} className="text-[color:var(--dms-navy)]/50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// --------- Inventory tab ---------
function InventoryTab({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ slug: "", title: "", qty: 20, threshold: 5 });
  const { toast } = useToast();

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/inventory`, { headers: { "X-Admin-Password": token } });
      setItems(data.inventory);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/inventory`, { slug: form.slug, title: form.title, qty: parseInt(form.qty), threshold: parseInt(form.threshold) }, { headers: { "X-Admin-Password": token } });
      setForm({ slug: "", title: "", qty: 20, threshold: 5 });
      fetchItems();
      toast({ title: "Inventory saved" });
    } catch (err) { toast({ title: err.response?.data?.detail || "Save failed", variant: "destructive" }); }
  };

  const del = async (slug) => {
    try {
      await axios.delete(`${API}/admin/inventory/${slug}`, { headers: { "X-Admin-Password": token } });
      fetchItems();
    } catch { toast({ title: "Delete failed", variant: "destructive" }); }
  };

  return (
    <>
      <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-6">
        <h3 className="font-serif text-xl text-[color:var(--dms-navy)]">Track stock for a product</h3>
        <p className="mt-2 text-sm text-[color:var(--dms-navy)]/60">When any item drops at or below its threshold after a paid order, we'll email the owner immediately.</p>
        <form onSubmit={save} className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="Product slug (e.g. god-gidence-t-shirt)" className="sm:col-span-2 rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--dms-peach)]" />
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (optional)" className="sm:col-span-2 rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-2.5 text-sm" />
          <input required type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} placeholder="Qty" className="rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-2.5 text-sm" />
          <input required type="number" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })} placeholder="Alert threshold" className="rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-2.5 text-sm" />
          <button type="submit" className="btn-peach sm:col-span-2 justify-center"><Plus className="w-4 h-4" /> Save stock</button>
        </form>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 overflow-hidden">
        {loading ? <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--dms-peach)] mx-auto" /></div>
          : items.length === 0 ? <div className="p-12 text-center text-sm text-[color:var(--dms-navy)]/60">No inventory tracked yet.</div>
          : (
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--dms-cream-2)] text-left"><tr><Th>Product</Th><Th>Stock</Th><Th>Threshold</Th><Th>Status</Th><Th></Th></tr></thead>
            <tbody className="divide-y divide-[color:var(--dms-navy)]/5">
              {items.map((it) => {
                const low = it.qty <= it.threshold;
                return (
                  <tr key={it.slug}>
                    <td className="px-5 py-4"><div className="text-[color:var(--dms-navy)]">{it.title || it.slug}</div><div className="text-[11px] text-[color:var(--dms-navy)]/55 font-mono">{it.slug}</div></td>
                    <td className="px-5 py-4 font-serif text-xl text-[color:var(--dms-navy)]">{it.qty}</td>
                    <td className="px-5 py-4 text-[color:var(--dms-navy)]/70">{it.threshold}</td>
                    <td className="px-5 py-4"><span className={`inline-block text-[10px] tracking-widest uppercase font-semibold px-2.5 py-1 rounded-full ${low ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{low ? "Low" : "OK"}</span></td>
                    <td className="px-5 py-4 text-right"><button onClick={() => del(it.slug)} className="text-[color:var(--dms-navy)]/50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function Th({ children }) {
  return <th className="px-5 py-3 text-[11px] tracking-widest uppercase font-semibold">{children}</th>;
}

function Pill({ status }) {
  const map = { paid: "bg-emerald-100 text-emerald-800", pending: "bg-amber-100 text-amber-800", refunded: "bg-rose-100 text-rose-800" };
  return <span className={`inline-block text-[10px] tracking-widest uppercase font-semibold px-2.5 py-1 rounded-full ${map[status] || "bg-slate-100 text-slate-800"}`}>{status}</span>;
}
